(() => {
  const pickerSelector = 'variant-radios, variant-selects';
  const groupSelector = 'fieldset, select';

  function getVariants(picker) {
    const data = picker.querySelector('script[type="application/json"]');

    if (!data) return [];

    try {
      return JSON.parse(data.textContent);
    } catch (error) {
      return [];
    }
  }

  function getGroups(picker) {
    return Array.from(picker.querySelectorAll(groupSelector));
  }

  function getSelectedValue(group) {
    if (group.tagName === 'SELECT') return group.value;
    return group.querySelector('input:checked')?.value;
  }

  function formatProductCardPrice(price, card) {
    const amount = Number(price);

    if (!Number.isFinite(amount)) return null;

    const sample = card.querySelector('.prd-price-regular, .prd-price')?.textContent || '';
    const formatted = new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount / 100);

    if (sample.includes('€')) return `${formatted}€`;
    if (sample.includes('$')) return `$${formatted}`;
    if (sample.includes('£')) return `£${formatted}`;

    return formatted;
  }

  function renderProductCardPrice(card, formattedPrice) {
    card.querySelectorAll('.prd-price-regular').forEach((price) => {
      if (price.textContent.trim() !== formattedPrice) {
        price.textContent = formattedPrice;
      }
    });

    card.querySelectorAll('.prd-price').forEach((price) => {
      if (!price.querySelector('.prd-price-regular') && price.textContent.trim() !== formattedPrice) {
        price.textContent = formattedPrice;
      }
    });
  }

  function updateProductCardPrice(picker, variant) {
    const card = picker.closest('product-card');

    if (!card || !variant) return;

    const formattedPrice = formatProductCardPrice(variant.price, card);

    if (!formattedPrice) return;

    card.dataset.selectedVariantPrice = formattedPrice;
    renderProductCardPrice(card, formattedPrice);
  }

  function syncProductCardPrices() {
    document.querySelectorAll('product-card[data-selected-variant-price]').forEach((card) => {
      renderProductCardPrice(card, card.dataset.selectedVariantPrice);
    });
  }

  function syncActiveOptionStyles(picker) {
    getGroups(picker).forEach((group) => {
      if (group.tagName === 'SELECT') return;

      group.querySelectorAll('input').forEach((input) => {
        const label = input.closest('label');
        const active = input.checked;

        input.toggleAttribute('checked', active);
        label?.classList.toggle('swatch-active', active);

        if (label) {
          label.style.transition = 'none';
          label.style.borderColor = active
            ? 'var(--product-swatches-border-active-color)'
            : 'var(--product-swatches-border-color)';
          label.style.color = active
            ? 'var(--product-swatches-active-color)'
            : 'var(--product-swatches-color)';
          label.style.backgroundColor = active
            ? 'var(--product-swatches-active-bg)'
            : 'var(--product-swatches-bg)';
        }
      });
    });
  }

  function enableCompatibleOptions(picker) {
    syncActiveOptionStyles(picker);

    const variants = getVariants(picker);
    const groups = getGroups(picker);
    const firstValue = groups.length ? getSelectedValue(groups[0]) : null;

    if (!variants.length || !firstValue) return;

    const currentOptions = groups.map(getSelectedValue);
    const currentVariant = variants.find((variant) => {
      return variant.options.every((option, index) => option === currentOptions[index]);
    });

    groups.forEach((group, index) => {
      if (index === 0) return;

      if (group.tagName === 'SELECT') {
        Array.from(group.options).forEach((option) => {
          option.disabled = !variants.some((variant) => {
            return variant.available && variant.options[0] === firstValue && variant.options[index] === option.value;
          });
        });
        return;
      }

      group.querySelectorAll('input').forEach((input) => {
        const isCompatible = variants.some((variant) => {
          return variant.available && variant.options[0] === firstValue && variant.options[index] === input.value;
        });

        input.closest('label')?.classList.toggle('disabled', !isCompatible);
      });
    });
  }

  function enableAllCompatibleOptions() {
    document.querySelectorAll(pickerSelector).forEach(enableCompatibleOptions);
  }

  document.addEventListener('change', (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const picker = target.closest(pickerSelector);

    if (!picker) return;

    const groups = getGroups(picker);
    const changedGroup = target.closest(groupSelector);
    const changedIndex = groups.indexOf(changedGroup);

    requestAnimationFrame(enableAllCompatibleOptions);

    if (changedIndex < 0) return;

    const variants = getVariants(picker);
    const currentOptions = groups.map(getSelectedValue);
    const exactVariant = variants.find((variant) => {
      return variant.options.every((option, index) => option === currentOptions[index]);
    });

    if (exactVariant) {
      updateProductCardPrice(picker, exactVariant);
      return;
    }

    const selectedValue = currentOptions[changedIndex];
    const compatibleVariants = variants.filter((variant) => {
      return variant.options[changedIndex] === selectedValue;
    });
    const availableVariants = compatibleVariants.filter((variant) => variant.available);
    const candidates = availableVariants.length ? availableVariants : compatibleVariants;

    if (!candidates.length) return;

    let compatibleVariant = candidates[0];
    let compatibleScore = -1;

    candidates.forEach((variant) => {
      const score = variant.options.reduce((total, option, index) => {
        if (index === changedIndex) return total;
        return total + (currentOptions[index] === option ? 1 : 0);
      }, 0);

      if (score > compatibleScore) {
        compatibleVariant = variant;
        compatibleScore = score;
      }
    });

    groups.forEach((group, index) => {
      const value = compatibleVariant.options[index];

      if (group.tagName === 'SELECT') {
        group.value = value;
        return;
      }

      group.querySelectorAll('input').forEach((input) => {
        input.checked = input.value === value;
      });
    });

    syncActiveOptionStyles(picker);
    updateProductCardPrice(picker, compatibleVariant);
  }, true);

  document.addEventListener('shopify:section:load', enableAllCompatibleOptions);
  window.addEventListener('load', enableAllCompatibleOptions, { once: true });

  const pickerObserver = new MutationObserver((mutations) => {
    const pickerChanged = mutations.some((mutation) => {
      return mutation.target instanceof Element && mutation.target.closest(pickerSelector);
    });

    const productCardChanged = mutations.some((mutation) => {
      return mutation.target instanceof Element && mutation.target.closest('product-card[data-selected-variant-price]');
    });

    if (pickerChanged) queueMicrotask(enableAllCompatibleOptions);
    if (productCardChanged) queueMicrotask(syncProductCardPrices);
  });

  pickerObserver.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['class']
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enableAllCompatibleOptions, { once: true });
  } else {
    queueMicrotask(enableAllCompatibleOptions);
  }
})();
